// @flow

import React, { lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { useAtom } from '@reatom/react'

import { StepsAtom } from '@bfg/atoms/Steps'
import { debugMode, relativeRoot } from '@bfg/common'
import { Loader } from '@bfg/components/screens/Loader'
import { useSignIn } from '@bfg/effects/signIn'
import { usePaymentExpired, usePaymentLoadingState, usePaymentPayed } from '@bfg/contexts/PaymentStateContext'
import { useSubscriber, useSubscriberLoadingState } from '@bfg/contexts/SubscriberStateContext'
import { usePageSizeExperiment } from '@bfg/contexts/ExperimentsContext'
import { useSkipStepExperiment } from '@bfg/contexts/ExperimentsContext'
import { SkipStepExperimentEnum } from '@bfg/enums/SkipStepExperimentEnum'
import { StepsSkipExperimentAtom } from '@bfg/atoms/StepsSkipExperiment'

const rootPath = `${relativeRoot}/:locale(de|es|fr|hi|pl|pt|ru|tr|zh|what-would-our-baby-look-like)?`

// Первый экран намеренно НЕ ленивый. Пока он был в lazy-чанке, его загрузка
// не могла начаться, пока не выполнится main.js: в замере сеть простаивала
// с 1045 до 1700 мс, а первая отрисовка ждала лишний round-trip. Остальные
// экраны ленивыми и остаются — их показывают уже после первого взаимодействия.
import FirstParent from '@bfg/components/screens/FirstParent'
const SecondParent = lazy(() => import('@bfg/components/screens/SecondParent'))
const KidConfig = lazy(() => import('@bfg/components/screens/KidConfig'))
const UnifiedPayScreen = lazy(() => import('@bfg/components/screens/UnifiedPayScreen'))
const UnifiedResultScreen = lazy(() => import('@bfg/components/screens/UnifiedResultScreen'))
const ProfileScreen = lazy(() => import('@bfg/components/screens/Profile'))
// const BluesnapScreen = lazy(() => import('@bfg/components/screens/BluesnapScreen'))
const ContactUs = lazy(() => import('@bfg/components/ContactUs'))
const AboutUs = lazy(() => import('@bfg/components/AboutUs'))
const TermsOfService = lazy(() => import('@bfg/components/TermsOfService'))
const Feedback = lazy(() => import('@bfg/components/Feedback'))
const DebugComponent = lazy(() => import('@bfg/components/utils/debug'))
const PageNotFoundComponent = lazy(() => import('@bfg/components/404.js'))
const WikiScreen = lazy(() => import('@bfg/components/screens/WikiScreen'))

export const Screen = () => {
  const paymentPayed = usePaymentPayed()
  const paymentExpired = usePaymentExpired()
  const isPaymentsLoaded = usePaymentLoadingState()
  const isSubscriberLoaded = useSubscriberLoadingState()
  const subscriber = useSubscriber()
  const pageSizeExperiment = usePageSizeExperiment()

  // exp
  const stepSkipExperiment = useSkipStepExperiment()
  const stepsSkipExperimentState = useAtom(StepsSkipExperimentAtom)
  const showPayScreenSkipExperiment = stepsSkipExperimentState.currentStepId === 3 && !(paymentPayed || paymentExpired)
  const showResultScreenSkipExperiment = stepsSkipExperimentState.currentStepId === 3 && (paymentPayed || paymentExpired)
  const showPreparedPayScreenSkipExperiment = isSubscriberLoaded && showPayScreenSkipExperiment && isPaymentsLoaded
  const showPreparedResultScreenSkipExperiment = isSubscriberLoaded && showResultScreenSkipExperiment
// experiment

  const steps = useAtom(StepsAtom)

  const signInHandler = useSignIn()
  const signInPopUp = signInHandler.signInPopUp()
  const otpPopUp = signInHandler.OTPPopUp()

  const showPayScreen = steps.currentStepId === 4 && !(paymentPayed || paymentExpired)
  const showResultScreen = steps.currentStepId === 4 && (paymentPayed || paymentExpired)

  const showPreparedPayScreen = isSubscriberLoaded && showPayScreen && isPaymentsLoaded
  const showPreparedResultScreen = isSubscriberLoaded && showResultScreen

  // console.log({
  //   showPreparedResultScreenSkipExperiment,
  //   showPreparedPayScreenSkipExperiment,
  //   isSubscriberLoaded,
  //   paymentPayed,
  //   paymentExpired,
  //   isPaymentsLoaded
  // })

  return (<>
    <div className={`section-main page-size-experiment`}>
      <Suspense fallback={<Loader/>}>
        <Switch>
          <Route
            exact
            strict
            sensitive
            path="/:slug1(.)*:slug2([A-Z]):slug3(.)*(/?)"
            render={props => <Redirect to={props.location.pathname.toLowerCase()}/>}
          />
          <Route
            exact
            strict
            sensitive
            path="/:slug1*:slug2([A-Z]):slug3*(/?)"
            render={props => <Redirect to={props.location.pathname.toLowerCase()}/>}
          />

          {
            stepSkipExperiment === SkipStepExperimentEnum.skip
            &&
            <Route exact={true} path={`${rootPath}/`}>
              {/* Ждать subscriber ради первого экрана незачем: разметка от него не
                  зависит, а загрузка фото подставит актуальный id в момент клика
                  (см. uploadImage). Раньше первая отрисовка была привязана к
                  скорости ответа /api/subscriber. */}
              {stepsSkipExperimentState.currentStepId === 1
                && <FirstParent subscriberId={subscriber?.id}/>}
              {stepsSkipExperimentState.currentStepId === 2
                && isSubscriberLoaded
                && <SecondParent subscriberId={subscriber.id}/>}
              {/*{steps.currentStepId === 3 && isSubscriberLoaded && <KidConfig/>}*/}
              {showPreparedPayScreenSkipExperiment
                && !showResultScreenSkipExperiment
                && isSubscriberLoaded
                && <UnifiedPayScreen/>}
              {!showPreparedPayScreenSkipExperiment
                && showPreparedResultScreenSkipExperiment
                && <UnifiedResultScreen/>}
              {/*{steps.currentStepId === 4 && !showPreparedPayScreen && !showPreparedResultScreen && <Loader/>}*/}
              {stepsSkipExperimentState.currentStepId === 3
                && !showPreparedPayScreenSkipExperiment
                && !showPreparedResultScreenSkipExperiment
                && <Loader/>}
            </Route>
          }

          {
            stepSkipExperiment === SkipStepExperimentEnum.dontSkip
            &&
            <Route exact={true} path={`${rootPath}/`}>
              {/* См. комментарий в ветке skip — первый экран не ждёт subscriber. */}
              {steps.currentStepId === 1
                && <FirstParent subscriberId={subscriber?.id}/>}
              {steps.currentStepId === 2
                && isSubscriberLoaded
                && <SecondParent subscriberId={subscriber.id}/>}
              {steps.currentStepId === 3
                && isSubscriberLoaded
                && <KidConfig/>}
              {showPreparedPayScreen
                && !showResultScreen
                && isSubscriberLoaded
                && <UnifiedPayScreen/>}
              {!showPreparedPayScreen
                && showPreparedResultScreen
                && <UnifiedResultScreen/>}
              {steps.currentStepId === 4
                && !showPreparedPayScreen
                && !showPreparedResultScreen
                && <Loader/>}
            </Route>
          }

          <Route path={`${rootPath}/terms-of-service`} component={TermsOfService}/>
          <Route path={`${rootPath}/contact-us`} component={ContactUs}/>
          <Route path={`${rootPath}/about-us`} component={AboutUs}/>
          <Route path={`${rootPath}/feedback`} component={Feedback}/>
          <Route path={`${rootPath}/profile`} component={ProfileScreen}/>
          <Route path={`${rootPath}/wiki`} component={WikiScreen}/>

          {/*<Route path={`${rootPath}/bs`} component={BluesnapScreen}/>*/}

          <Route component={PageNotFoundComponent} status={404}/>

        </Switch>
      </Suspense>
      {debugMode === 'true' && (<Suspense fallback={<></>}>
        <DebugComponent/>
      </Suspense>)}
      {signInPopUp}
      {otpPopUp}
    </div>
  </>)
}
